import {useEffect, useMemo, useState} from 'react';
import type {ProductCatalogItem, StoreEconomics, UnitEconomics} from '@ozon-ai/ozon/contracts';
import {api} from '../../../api/client';
import {usePeriodPicker, periodSelectionKey} from '../../../components/period-picker/use-period-picker';
import {usePeriodPresets} from '../../../components/period-picker/use-period-presets';

export function totalsToUnitEconomics(totals: StoreEconomics['totals']): UnitEconomics {
	return {
		...totals,
		sku: '',
		offerId: null,
	};
}

export function useStoreEconomicsPage() {
	const {presets, primaryPresetId, loading: presetsLoading} = usePeriodPresets();
	const period = usePeriodPicker({
		presets,
		primaryPresetId,
		presetsReady: !presetsLoading,
	});
	const [catalog, setCatalog] = useState<ProductCatalogItem[] | null>(null);
	const [data, setData] = useState<StoreEconomics | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const selectionKey = periodSelectionKey(period.selection);

	useEffect(() => {
		let cancelled = false;
		void api
			.get<ProductCatalogItem[]>('/products/catalog')
			.then((items) => {
				if (!cancelled) setCatalog(items);
			})
			.catch(() => {
				if (!cancelled) setCatalog([]);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const skuLabels = useMemo(() => {
		const map = new Map<string, string>();
		for (const item of catalog ?? []) {
			if (item.ozonSku) map.set(item.ozonSku, item.name);
		}
		return map;
	}, [catalog]);

	useEffect(() => {
		if (!period.selection) {
			setData(null);
			setError(period.localError);
			setLoading(false);
			return;
		}

		let cancelled = false;
		setLoading(true);
		setError(null);
		void api
			.post<StoreEconomics>('/store-economics', {period: period.selection})
			.then((next) => {
				if (!cancelled) setData(next);
			})
			.catch((err) => {
				if (!cancelled) {
					setData(null);
					setError(err instanceof Error ? err.message : 'Не удалось посчитать экономику магазина');
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [period.localError, period.selection, selectionKey]);

	const hasSales =
		data != null &&
		(data.totals.deliveredUnits > 0 || data.totals.returnedUnits > 0 || data.skus.length > 0);

	return {
		period,
		catalog,
		data,
		loading,
		error,
		hasSales,
		skuLabels,
		waterfallRow: data ? totalsToUnitEconomics(data.totals) : null,
	};
}
